import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PageShell } from "@/components/SidebarLayout";
import HorizonSelector from "@/components/HorizonSelector";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";

// Interest rate stored as fraction (0.12 → "12%"). Trims trailing zeros so
// 0.129 shows as "12.9%", 0.1275 as "12.75%".
function formatRateInline(rate) {
  if (!rate) return "0%";
  return `${(rate * 100).toFixed(3).replace(/\.?0+$/, "")}%`;
}

/**
 * Read-only debt schedule. Every number here is calculated live from
 * Collections + Loan Facilities config — no persisted schedule.
 */
export default function DebtSchedule() {
  const [horizon, setHorizon] = useState(18);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/loans/schedule", { params: { horizon } });
      setData(data);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  }, [horizon]);

  useEffect(() => { load(); }, [load]);

  const monthLabels = data?.month_labels || [];
  const months = data?.months || [];

  return (
    <PageShell
      testId="loans-schedule-page"
      kicker="Loans"
      title="Debt Schedule"
      hideProjectFilter
      rangeLabel={`${horizon}m`}
      rangeMonths={months}
      actions={<HorizonSelector value={horizon} onChange={setHorizon} months={data?.months} />}
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-3xl">
        Live-calculated from Collections + Loan Facilities. Edit a facility&apos;s slabs or the underlying sales bookings and this page updates on next load — nothing persisted here.
      </p>

      {/* Portfolio totals */}
      {data && (
        <div className="cf-card p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-3">Portfolio totals ({horizon}m)</div>
          <div className="grid grid-cols-3 gap-4">
            <Kpi label="Total Interest" value={data.portfolio.total_interest} accent="var(--cf-accent)" testId="debt-total-interest" />
            <Kpi label="Total Principal Swept" value={data.portfolio.total_principal} accent="#6366F1" testId="debt-total-principal" />
            <Kpi label="Total Debt Service" value={data.portfolio.total_debt_service} accent="#10B981" testId="debt-total-service" />
          </div>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/70">
                  <th className="text-left font-semibold px-3 py-2 sticky left-0 z-10" style={{ background: "var(--cf-surface)" }}>Portfolio</th>
                  {monthLabels.map((l) => <th key={l} className="text-right font-semibold px-2 py-2 min-w-[60px]">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                <PortfolioRow label="Interest" data={data.portfolio.interest} months={months} />
                <PortfolioRow label="Principal Swept" data={data.portfolio.principal} months={months} />
                <PortfolioRow label="Debt Service" data={data.portfolio.debt_service} months={months} bold />
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-facility */}
      {data?.facilities.length === 0 && (
        <div className="cf-card p-8 text-center text-sm text-muted-dark">
          No facilities configured yet. Add one on <a href="/loans/facilities" className="text-ink underline">Loan Facilities</a>.
        </div>
      )}
      {data?.facilities.map((f) => (
        <div key={f.id} className="cf-card p-5" data-testid={`debt-facility-${f.name}`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">{f.lender || "—"}</div>
              <div className="font-display text-lg text-ink font-bold">{f.name}</div>
              <div className="text-xs text-muted-dark">
                {f.sweep_basis === "collections" ? "Sweep: cumulative collections" : "Sweep: loan age"} · rate {formatRateInline(f.rate)}
                {f.project_names?.length > 0 && ` · linked ${f.project_names.join(", ")}`}
              </div>
            </div>
            <div className="text-right text-xs">
              <div className="text-muted-light">Total Debt Service</div>
              <div className="text-ink font-bold num">{formatMoney(f.row_totals.debt_service)}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/70">
                  <th className="text-left font-semibold px-3 py-2 sticky left-0 z-10" style={{ background: "var(--cf-surface)" }}>Row</th>
                  {monthLabels.map((l) => <th key={l} className="text-right font-semibold px-2 py-2 min-w-[60px]">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                <Row label="Opening" data={f.rows.opening} months={months} muted />
                <Row label="Drawdown" data={f.rows.drawdown} months={months} />
                <Row label="Principal Swept" data={f.rows.principal} months={months} />
                <Row label="Interest" data={f.rows.interest} months={months} />
                <Row label="Closing" data={f.rows.closing} months={months} bold />
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </PageShell>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div className="p-4 rounded-xl" style={{ background: "var(--cf-surface)" }}>
      <div className="flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: accent }} />
        <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">{label}</div>
      </div>
      <div data-testid={testId} className="mt-1 num text-xl md:text-2xl font-bold text-ink">{formatMoney(value)}</div>
    </div>
  );
}

function Row({ label, data, months, bold, muted }) {
  return (
    <tr className="border-b border-hairline/40">
      <td className={`px-3 py-1.5 sticky left-0 ${bold ? "font-bold text-ink" : "text-ink"}`} style={{ background: "var(--cf-surface)" }}>{label}</td>
      {months.map((m) => (
        <td key={m} className={`px-2 py-1.5 text-right num ${bold ? "font-bold" : muted ? "text-muted-dark" : ""}`}>
          {data[m] ? formatMoney(data[m]) : "–"}
        </td>
      ))}
    </tr>
  );
}
function PortfolioRow({ label, data, months, bold }) {
  return <Row label={label} data={data} months={months} bold={bold} />;
}
